import React, { useEffect, useRef, useState } from "react";
import request from "request"
import Flash from "flash"

import Button from "Button"
import Modal from "Modal"

const POLL_INTERVAL_MS = 1500
const POLL_TIMEOUT_MS = 60_000

const isInterac = (order) =>
  order?.payment?.paymentMethodType === "interac_present"

const RefundConfirmModal = ({ order, onClose, onRefunded }) => {
  const [step, setStep] = useState("confirm") // confirm | submitting | processing | success | failed
  const [amount, setAmount] = useState(order.total ? String(order.total) : "")
  const [errorMessage, setErrorMessage] = useState(null)
  const [refundId, setRefundId] = useState(null)
  const [pollAttempt, setPollAttempt] = useState(0)
  const pollTimeoutRef = useRef(null)
  const startedAtRef = useRef(null)

  const interac = isInterac(order)

  useEffect(() => {
    return () => {
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current)
    }
  }, [])

  const submitRefund = () => {
    if (!order || !order.id) return
    setStep("submitting")
    setErrorMessage(null)

    const url = interac
      ? `/orders/${order.id}/send_refund_to_reader`
      : `/orders/${order.id}/refund`

    request.post(url, { amount })
      .then(({ data }) => {
        if (data && data.success) {
          const refund = data.refund
          if (refund && refund.id) setRefundId(refund.id)
          startedAtRef.current = Date.now()

          // Non-Interac path completes synchronously on the server, so the
          // refund (when present) will be :success. The Interac path may
          // return :processing while the reader is waiting for a tap.
          if (!refund || refund.status === "success") {
            setStep("success")
            Flash.broadcast("success", "Refund completed")
          } else if (refund.status === "failed") {
            setStep("failed")
            setErrorMessage(refund.failureMessage || "Refund failed")
          } else {
            setStep("processing")
            schedulePoll(refund.id)
          }
        } else {
          setStep("failed")
          setErrorMessage((data && data.error) || "Refund failed")
        }
      })
      .catch((error) => {
        const msg = error?.response?.data?.error || "Refund failed"
        setStep("failed")
        setErrorMessage(msg)
      })
  }

  const schedulePoll = (id) => {
    pollTimeoutRef.current = setTimeout(() => pollRefund(id), POLL_INTERVAL_MS)
  }

  const pollRefund = (id) => {
    if (!id) return
    request.get(`/refunds/${id}`)
      .then(({ data }) => {
        // The controller wraps the payload under `refund:`
        // (render_data(refund: ...)), same as the POST response.
        const refund = data && data.refund
        if (!refund) return
        if (refund.status === "success") {
          setStep("success")
          Flash.broadcast("success", "Refund completed")
        } else if (refund.status === "failed") {
          setStep("failed")
          setErrorMessage(refund.failureMessage || "Refund failed")
        } else {
          const elapsed = Date.now() - (startedAtRef.current || Date.now())
          if (elapsed >= POLL_TIMEOUT_MS) {
            setStep("failed")
            setErrorMessage("Timed out waiting for reader. Try canceling and retrying.")
            return
          }
          setPollAttempt((n) => n + 1)
          schedulePoll(id)
        }
      })
      .catch(() => {
        setPollAttempt((n) => n + 1)
        schedulePoll(id)
      })
  }

  const cancelRefund = () => {
    if (!refundId) return
    request.delete(`/refunds/${refundId}`)
      .then(({ data }) => {
        const refund = data && data.refund
        setStep("failed")
        setErrorMessage((refund && refund.failureMessage) || "Cancelled by operator.")
      })
      .catch(() => {
        Flash.broadcast("error", "Failed to cancel refund")
      })
  }

  const handleClose = () => {
    if (step === "success") onRefunded()
    else onClose()
  }

  const title =
    step === "confirm" ? `Refund order #${order.number}` :
    step === "submitting" ? (interac ? "Sending refund to reader…" : "Processing refund…") :
    step === "processing" ? "Awaiting customer card tap" :
    step === "success" ? "Refund completed" :
    "Refund failed"

  return (
    <Modal
      open={true}
      title={title}
      toggleModal={step === "processing" ? null : handleClose}
      actions={renderActions({ step, submitRefund, cancelRefund, handleClose })}
    >
      { step === "confirm" && (
        <div>
          <p className="mb-3">
            { interac
              ? "This will start an Interac refund on the connected card reader. The customer must tap the same card used for the original payment."
              : "This will refund the original card. The card reader is not required." }
          </p>
          <div className="field">
            <label className="label">Refund amount</label>
            <div className="control">
              <input
                type="text"
                inputMode="decimal"
                className="input"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
            <p className="help">Original total: ${order.total}</p>
          </div>
        </div>
      )}

      { step === "submitting" && (
        <p>{ interac ? "Sending request to the reader…" : "Processing refund…" }</p>
      )}

      { step === "processing" && (
        <div>
          <p className="mb-2">Ask the customer to tap their card on the reader.</p>
          <p className="has-text-grey is-size-7">Polling… attempt {pollAttempt + 1}</p>
        </div>
      )}

      { step === "success" && (
        <p>Refund of ${amount} completed.</p>
      )}

      { step === "failed" && (
        <p className="has-text-danger">{errorMessage}</p>
      )}
    </Modal>
  )
}

const renderActions = ({ step, submitRefund, cancelRefund, handleClose }) => {
  if (step === "confirm") {
    return (
      <div className="is-flex is-justify-content-flex-end" style={{ width: "100%" }}>
        <Button text="Cancel" className="is-outlined mr-2" onClick={handleClose} />
        <Button text="Refund" color="red" onClick={submitRefund} />
      </div>
    )
  }

  if (step === "processing") {
    return (
      <div className="is-flex is-justify-content-flex-end" style={{ width: "100%" }}>
        <Button text="Cancel on reader" className="is-outlined" color="red" onClick={cancelRefund} />
      </div>
    )
  }

  if (step === "submitting") return null

  return (
    <div className="is-flex is-justify-content-flex-end" style={{ width: "100%" }}>
      <Button text="Close" onClick={handleClose} />
    </div>
  )
}

export default RefundConfirmModal
